import React from "react";
import "./SeatingChart.css";

interface Person {
  name: string;
  tableId: number;
  seatNumber: number;
}

interface SeatingChartProps {
  people: Person[];
}

const SeatingChart: React.FC<SeatingChartProps> = ({ people }) => {
  // Group people by table
  const peopleByTable = people.reduce((acc, person) => {
    if (!acc[person.tableId]) {
      acc[person.tableId] = [];
    }
    acc[person.tableId].push(person);
    return acc;
  }, {} as Record<number, Person[]>);

  // Sort people within each table by seat number
  Object.keys(peopleByTable).forEach((tableId) => {
    peopleByTable[parseInt(tableId)].sort(
      (a, b) => a.seatNumber - b.seatNumber
    );
  });

  const renderRoundTable = (tableId: number) => {
    const tablePeople = peopleByTable[tableId] || [];
    const seatCount = tablePeople.length > 0 ? tablePeople.length : 8;

    // Calculate position for each seat around the circle
    const getSeatPosition = (index: number, total: number) => {
      const radius = 40; // Distance from center to seat center
      const angleOffset = -90; // Start from top (12 o'clock position)
      const angle = (360 / total) * index + angleOffset;
      const radian = (angle * Math.PI) / 180;

      const x = 50 + radius * Math.cos(radian); // 50% is center
      const y = 50 + radius * Math.sin(radian);

      return {
        left: `${x}%`,
        top: `${y}%`,
        transform: 'translate(-50%, -50%)'
      };
    };

    return (
      <div key={tableId} className="round-table">
        <div className="round-table-container">
          <div className="table-surface">
            <span className="table-number">{tableId}</span>
          </div>
          <div className="seats-container">
            {Array.from({ length: seatCount }, (_, index) => {
              const person = tablePeople.find(
                (p) => p.seatNumber === index + 1
              );
              const position = getSeatPosition(index, seatCount);

              return (
                <div
                  key={index}
                  className="seat"
                  style={position}
                  title={person ? `${person.name}` : "Empty seat"}
                >
                  <div className="seat-number">{index + 1}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderRectangularTable = () => {
    const tablePeople = peopleByTable[5] || [];
    const leftSide = tablePeople.filter((p) => p.seatNumber <= 6);
    const rightSide = tablePeople.filter((p) => p.seatNumber > 6);

    return (
      <div className="rectangular-table">
        <div className="rectangular-table-container">
          <div className="table-side left-side">
            {Array.from({ length: 6 }, (_, index) => {
              const person = leftSide.find((p) => p.seatNumber === index + 1);
              return (
                <div
                  key={`left-${index}`}
                  className="rect-seat"
                  title={person ? `${person.name}` : "Empty seat"}
                >
                  <div className="seat-number">{index + 1}</div>
                </div>
              );
            })}
          </div>

          <div className="rect-table-surface">
            <span className="table-label-center">Head Table</span>
          </div>

          <div className="table-side right-side">
            {Array.from({ length: 6 }, (_, index) => {
              const person = rightSide.find((p) => p.seatNumber === index + 7);
              return (
                <div
                  key={`right-${index}`}
                  className="rect-seat"
                  title={person ? `${person.name}` : "Empty seat"}
                >
                  <div className="seat-number">{index + 7}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="seating-chart">
      <h2>Seating Arrangement</h2>
      <div className="chart-with-labels">
        {/* Top Label - N Prospect Ave */}
        <div className="direction-label top-label">N Prospect Ave</div>

        <div className="chart-container">
          {/* Left Label - Wells St */}
          <div className="direction-label left-label">Wells St.</div>

          <div className="chart-content">
            <div className="round-tables-grid">
              {[1, 2, 3, 4].map((tableId) => renderRoundTable(tableId))}
            </div>
            <div className="rectangular-section">{renderRectangularTable()}</div>
          </div>

          {/* Right Label - Dance Floor */}
          <div className="direction-label right-label">Dance Floor</div>
        </div>

        {/* Bottom Label - Bar */}
        <div className="direction-label bottom-label">Bar</div>
      </div>
    </div>
  );
};

export default SeatingChart;
